import { Injectable, OnDestroy } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';

import {
    AdvancedSearchGroup,
    ApiQuery,
    ApiQueryBuilder,
    EmptyStructuredQuery,
    LogicalOperand,
    Operand,
    StructuredQuery
} from '@stylehaven/components';
import { toFormatBadges } from '../helpers/format-badges.helper';

import { ApiFragment } from '../entities/api-fragment.entity';
import { MetadataField } from '../entities/metadata-field.entity';
import { RecordType } from '../entities/metadata.entity';
import { RecordStatus } from '../entities/record-status.type';
import { RecordSearch } from '../entities/record-search.entity';
import { collectSearchResults, MAX_COLLECTED_RESULTS } from '../helpers/paged-search.helper';
import { LOADING_FORMATS, StukFormats, UNKNOWN_FORMATS } from '../entities/stuk-formats.entity';
import { RecordSandboxService } from '../sandbox/record-sandbox.service';

/**
 * HFBD-7a: the `Formaten` column's data. Also HFBD-10: the quick-preview icon's target, since it comes from
 * the exact same representaties — no separate lookup for it, one batched search answers both.
 *
 * A stuk's formats live on its representaties — one record each. Rather than one lookup per stuk, expanding
 * a dossier issues **one** request covering every stuk it just revealed, and this holds the grouped answer
 * for the cells to read.
 *
 * It caches per stuk id for the life of a result page: a follow-on "Toon meer stukken" batch only asks about
 * the stukken it added, and {@link reset} is what a new page calls to forget the previous one.
 */
@Injectable()
export class StukFormatsService implements OnDestroy {
    /**
     * Where paging stops. Deliberately **not** derived from how many representaties a stuk is expected to have:
     * that shape of bound (five per stuk) made an expansion of unusually rich stukken show no badges at all,
     * which is the opposite of what the ceiling is for. See {@link MAX_COLLECTED_RESULTS}.
     */
    static readonly MAX_REPRESENTATIONS: number = MAX_COLLECTED_RESULTS;

    private readonly destroy$: Subject<void> = new Subject<void>();

    private readonly formats$: BehaviorSubject<ReadonlyMap<string, StukFormats>> = new BehaviorSubject<
        ReadonlyMap<string, StukFormats>
    >(new Map());

    constructor(
        private readonly sandbox: RecordSandboxService,
        private readonly translate: TranslateService
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.formats$.complete();
    }

    /**
     * What a cell renders. Defaults to loading rather than to nothing: the column only exists on stuk rows,
     * and those only exist inside an expansion whose {@link load} is already on its way.
     */
    formatsFor(stukId: string): Observable<StukFormats> {
        return this.formats$.pipe(
            map(formats => formats.get(stukId) ?? LOADING_FORMATS),
            distinctUntilChanged()
        );
    }

    /**
     * One request for the stukken an expansion just revealed. A stuk already answered for is skipped — but a
     * stuk left `unknown` is asked about again, so the expansion's own retry repairs its badges too rather
     * than leaving the cells blank for the rest of the session.
     */
    load(stukIds: string[]): void {
        const known: ReadonlyMap<string, StukFormats> = this.formats$.value;
        const pending: string[] = [...new Set(stukIds)].filter(
            stukId => (known.get(stukId) ?? UNKNOWN_FORMATS).status === 'unknown'
        );

        if (pending.length === 0) {
            return;
        }

        this.patch(pending.map(stukId => [stukId, LOADING_FORMATS]));

        collectSearchResults(
            (startIndex, nrOfResults) =>
                this.sandbox.search(this.representationSearch(pending, startIndex, nrOfResults)),
            StukFormatsService.MAX_REPRESENTATIONS
        )
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: representations => this.onRepresentationsLoaded(pending, representations),
                error: () => this.patch(pending.map(stukId => [stukId, UNKNOWN_FORMATS]))
            });
    }

    /** A new result page shares nothing with the previous one, and its stukken are no longer on screen. */
    reset(): void {
        this.formats$.next(new Map());
    }

    private onRepresentationsLoaded(pending: string[], representations: ApiFragment[] | null): void {
        // An answer that could not be completed is missing an unknown subset of the representaties, so no stuk
        // in the batch can be trusted — and half a stuk's badges is a worse answer than none. It is worth saying
        // out loud: on screen this is indistinguishable from a batch of stukken that genuinely have no formats.
        if (!representations) {
            console.warn(
                `Formaten (HFBD-7a): the representaties of ${pending.length} stukken could not be listed in full` +
                    ` (cap ${StukFormatsService.MAX_REPRESENTATIONS}); those rows show no badges.`
            );
            this.patch(pending.map(stukId => [stukId, UNKNOWN_FORMATS]));

            return;
        }

        const grouped: Map<string, ApiFragment[]> = new Map(pending.map(stukId => [stukId, []]));

        representations.forEach(representation => {
            const stukId: string | null = this.toOwnerStukId(representation, pending);

            if (stukId) {
                grouped.get(stukId)?.push(representation);
            }
        });

        this.patch(
            [...grouped].map(([stukId, ownRepresentations]) => [
                stukId,
                {
                    status: 'ready',
                    badges: toFormatBadges(ownRepresentations, this.translate),
                    previewRecordId: this.toPreviewRecordId(ownRepresentations)
                }
            ])
        );
    }

    private toOwnerStukId(representation: ApiFragment, pending: string[]): string | null {
        const parentRecordId: string | undefined = representation.getParentRecordId();

        if (parentRecordId) {
            return pending.find(stukId => parentRecordId.startsWith(stukId)) ?? null;
        }

        const ancestors: string[] = representation
            .getReferenceCodes(RecordType.Document)
            .filter(ancestor => pending.includes(ancestor));

        return ancestors.length > 0 ? ancestors[ancestors.length - 1] : null;
    }

    /** The access representatie specifically: that is the one the viewer route shows for a preview. */
    private toPreviewRecordId(ownRepresentations: ApiFragment[]): string | null {
        const accessRepresentation: ApiFragment | undefined = ownRepresentations.find(
            representation => representation.metadata.Administrative?.IsAccess === true
        );

        return accessRepresentation?.getId() ?? null;
    }

    /**
     * Fetched on `ReferenceCodesDocument` and `ParentRecordId` rather than on the engine's `DataRecordId`,
     * which has no field definition in this tenant.
     *
     * Rejected representaties are excluded explicitly. Under `DataRecordId` they fell out on their own, since
     * the app reaches them through `RelationsIsCandidateChildOf` instead; whether they carry the new key is
     * unproven (probe 7), and either way a rejected copy is not a format the stuk exists in.
     *
     * No caller restrictions: a representatie is not a row the user can reach, and a page-level filter such
     * as *Alles in archief*'s `RecordStatus` would silently drop formats the stuk above genuinely has. What
     * the user may see is enforced by the base record query the backend injects itself.
     */
    private representationSearch(stukIds: string[], startIndex: number, nrOfResults: number): RecordSearch {
        const typeGroup: AdvancedSearchGroup = {
            LogicalOperand: LogicalOperand.And,
            Terms: [
                { DottedKey: MetadataField.RecordType, Operand: Operand.Include, Values: [RecordType.Representation] },
                { DottedKey: MetadataField.RecordStatus, Operand: Operand.Exclude, Values: [RecordStatus.Rejected] }
            ]
        };

        const parentGroup: AdvancedSearchGroup = {
            LogicalOperand: LogicalOperand.Or,
            Terms: [
                { DottedKey: MetadataField.ReferenceCodesDocument, Operand: Operand.Include, Values: stukIds },
                {
                    DottedKey: MetadataField.ParentRecordId,
                    Operand: Operand.Include,
                    Values: stukIds.map(stukId => `${stukId}*`)
                }
            ]
        };

        const structuredQuery: StructuredQuery = {
            ...EmptyStructuredQuery,
            AdvancedSearch: { LogicalOperand: LogicalOperand.And, Groups: [typeGroup, parentGroup] }
        };

        return {
            Q: ApiQueryBuilder.queryToLuceneQuery(new ApiQuery(structuredQuery)),
            StartIndex: startIndex,
            NrOfResults: nrOfResults
        };
    }

    private patch(entries: [string, StukFormats][]): void {
        const updated: Map<string, StukFormats> = new Map(this.formats$.value);
        entries.forEach(([stukId, formats]) => updated.set(stukId, formats));

        this.formats$.next(updated);
    }
}
